'use client';

import { FC, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { getUserLocale } from '@/hotdesk-ui/lib/language.service';
import { loadWebSubscriptionPlansOrNull } from '@/hotdesk-ui/services/subscription-availability.service';
import PlanCard from '@/hotdesk-ui/components/subscriptions-v4/PlanCard';
import { LoadingSpinner } from '@/hotdesk-ui/components/loading/LoadingSpinner';
import modalStyles from './Modal.module.scss';
import styles from './PlansPopup.module.scss';
export interface PlansPopupPlan {
  subscription_package_id: number | string;
  subscription_id: number | string;
  name: string;
  description: string;
  currency: string;
  price: number;
  list_notes?: string[];
  badge?: string;
  highlighted?: boolean;
}
interface PlansPopupProps {
  show: boolean;
  ctaLabel: string;
  /**
   * When provided, only plans strictly more expensive than the plan matching
   * this package id are shown (upgrade mode). The current plan is excluded.
   */
  upgradeFromPackageId?: string | null;
  /**
   * When provided, only these package ids are shown (e.g. checkout upgrade
   * remote-config country packages). Applied after upgrade filtering.
   */
  packageIds?: Array<number | string> | null;
  onClose: () => void;
  onSelectPlan: (plan: PlansPopupPlan) => void;
}
const formatPrice = (currency: string, price: number): string => `${price.toLocaleString()} ${currency}`;
const PlansPopup: FC<PlansPopupProps> = ({
  show,
  ctaLabel,
  upgradeFromPackageId,
  packageIds,
  onClose,
  onSelectPlan
}) => {
  const t = useTranslations();
  const [plans, setPlans] = useState<PlansPopupPlan[] | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!show) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [show, onClose]);
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    const loadPlans = async () => {
      try {
        setLoading(true);
        const languageCode = await getUserLocale();
        const payload = await loadWebSubscriptionPlansOrNull(languageCode);
        if (cancelled) return;
        const packages = payload?.packages as PlansPopupPlan[] | undefined ?? [];
        setPlans(packages);
      } catch (error) {
        console.error('Failed to load plans', error);
        if (!cancelled) setPlans([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadPlans();
    return () => {
      cancelled = true;
    };
  }, [show]);
  const visiblePlans = (() => {
    if (!plans) return [];
    let next = plans;
    if (upgradeFromPackageId) {
      const current = plans.find(plan => String(plan.subscription_package_id) === String(upgradeFromPackageId));
      if (!current) {
        next = plans.filter(plan => String(plan.subscription_package_id) !== String(upgradeFromPackageId));
      } else {
        next = plans.filter(plan => plan.price > current.price);
      }
    }
    if (packageIds && packageIds.length > 0) {
      const allowed = new Set(packageIds.map(id => String(id)));
      next = next.filter(plan => allowed.has(String(plan.subscription_package_id)));
    }
    return next;
  })();
  if (!show) return null;
  return <div className={modalStyles.backdrop} onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-sentry-component="PlansPopup" data-sentry-source-file="PlansPopup.tsx">
            <div className={styles.card} role="dialog" aria-modal="true" aria-label={ctaLabel}>
                <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t('my-account.cancel')}>
                    <X size={18} strokeWidth={2} data-sentry-element="X" data-sentry-source-file="PlansPopup.tsx" />
                </button>

                {loading || plans === null ? <div className={styles.stateWrap}>
                        <LoadingSpinner />
                    </div> : visiblePlans.length === 0 ? <div className={styles.stateWrap}>
                        <p className={styles.emptyText}>
                            {t('my-account.memberships.plans-popup-empty')}
                        </p>
                    </div> : <div className={styles.grid} data-count={visiblePlans.length}>
                        {visiblePlans.map(plan => <PlanCard key={String(plan.subscription_package_id)} name={plan.name} description={plan.description} price={formatPrice(plan.currency, plan.price)} badge={plan.badge} features={plan.list_notes ?? []} ctaLabel={ctaLabel} highlighted={plan.highlighted ?? false} onCtaClick={() => onSelectPlan(plan)} />)}
                    </div>}
            </div>
        </div>;
};
export default PlansPopup;